import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { Heading } from '@hopper/heading';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _excluded = ["styles"];
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
var CloseIcon = (function (_ref) {
  _ref.styles;
    var props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: "20",
    height: "20",
    viewBox: "1 0 46 46",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), /*#__PURE__*/React.createElement("circle", {
    cx: "24",
    cy: "23",
    r: "20"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "#fff",
    strokeWidth: "3",
    d: "M15.894 14.439L32.56 31.106m-.454-16.666L15.44 31.105"
  }));
});

var CloseModalButton = function CloseModalButton(_a) {
  var onClick = _a.onClick;
  return /*#__PURE__*/React.createElement("button", {
    className: "absolute hover:text-dark text-muted",
    style: {
      top: "0.5rem",
      right: "0.5rem"
    },
    onClick: onClick,
    "aria-label": "close"
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    className: "fill-current",
    style: {
      height: "1.5rem",
      width: "1.5rem"
    }
  }));
};
CloseModalButton.propTypes = {
  onClick: PropTypes.func.isRequired
};

var SIZES = {
  sm: "20rem",
  md: "30rem",
  lg: "40rem"
};
var POSITION_CLASSES = {
  top: "mx-auto mt-sm mb-auto",
  center: "m-auto"
};
var isClosingUncontrolled = function isClosingUncontrolled(check) {
  return !("isShowingCloseButton" in check);
};
var setOpenModalStyle = function setOpenModalStyle(element) {
  return element.classList.add("h-100", "overflow-y-hidden");
};
var setClosedModalStyle = function setClosedModalStyle(element) {
  return element.classList.remove("h-100", "overflow-y-hidden");
};
var Modal = function Modal(props) {
  if (isClosingUncontrolled(props)) {
    props = __assign(__assign({}, props), {
      isShowingCloseButton: true,
      onCloseButtonClicked: function onCloseButtonClicked(_event) {
        return {};
      }
    });
  } else {
    if (props.isShowingCloseButton && typeof props["onCloseButtonClicked"] !== "function") {
      throw new Error("onCloseButtonClicked prop is required when isShowingCloseButton is set");
    }
  }
  var children = props.children,
    _a = props.isOpen,
    isOpen = _a === void 0 ? false : _a,
    _b = props.size,
    size = _b === void 0 ? "md" : _b,
    _c = props.position,
    position = _c === void 0 ? "top" : _c,
    isShowingCloseButton = props.isShowingCloseButton,
    onCloseButtonClicked = props.onCloseButtonClicked,
    onOverlayClicked = props.onOverlayClicked,
    _d = props.overlayColor,
    overlayColor = _d === void 0 ? "rgba(1, 1, 1, 0.5)" : _d,
    _e = props.rootElementSelector,
    rootElementSelector = _e === void 0 ? "body" : _e;
  useEffect(function () {
    if (!rootElementSelector) {
      return;
    }
    var element = document.querySelector(rootElementSelector);
    if (!element) {
      return;
    }
    if (isOpen) {
      setOpenModalStyle(element);
    } else {
      setClosedModalStyle(element);
    }
    return function () {
      setClosedModalStyle(element);
    };
  }, [isOpen, rootElementSelector]);
  if (!isOpen) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "flex fixed z-100 h-full inset-0 auto-qa-panel justify-end auto-qa-panel-fully-visible w-full flex-grow"
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute w-full h-full",
    style: {
      backgroundColor: overlayColor
    },
    onClick: onOverlayClicked,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames("flex flex-col relative bg-lightest border-light rounded-lg shadow", POSITION_CLASSES[position]),
    style: {
      maxWidth: SIZES[size],
      maxHeight: "90vh",
      minHeight: "10rem",
      minWidth: "15rem"
    }
  }, isShowingCloseButton && /*#__PURE__*/React.createElement(CloseModalButton, {
    onClick: onCloseButtonClicked
  }), /*#__PURE__*/React.createElement("div", {
    className: classnames("overflow-y-auto py-3xl pl-lg", {
      "pr-4xl": isShowingCloseButton,
      "pr-lg": !isShowingCloseButton
    })
  }, children)));
};
Modal.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  isShowingCloseButton: PropTypes.bool,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  position: PropTypes.oneOf(["top", "center"]),
  onOverlayClicked: PropTypes.func.isRequired,
  overlayColor: PropTypes.string,
  rootElementSelector: PropTypes.string
};

var ModalHeader = function ModalHeader(_a) {
  var children = _a.children;
  return /*#__PURE__*/React.createElement(Heading, {
    size: 3
  }, children);
};
ModalHeader.propTypes = {
  children: PropTypes.node.isRequired
};

var ModalBody = function ModalBody(props) {
  return /*#__PURE__*/React.createElement("div", __assign({
    className: "my-md"
  }, props));
};

export { Modal, ModalBody, ModalHeader };
//# sourceMappingURL=index.es.js.map
